// RichTextEditor — tiptap is ~300 KB, so the editor itself is lazy-loaded; this wrapper is
// what pages import. Output is HTML; the SERVER sanitizes it (sanitizeRichText) before storing.
import { lazy, Suspense } from 'react'
import { cn } from './cn'
import { Skeleton } from './feedback'

export interface RichTextEditorProps {
  /** HTML ('' when empty). */
  value: string
  onChange: (html: string) => void
  placeholder?: string
  /** Min editor height in px (default 160). */
  minHeight?: number
  /** Upload an image file and return its public URL; enables the image button, paste and drop. */
  onUploadImage?: (file: File) => Promise<string>
  /** Heading levels offered (default [2, 3]). */
  headings?: Array<2 | 3 | 4>
  disabled?: boolean
  invalid?: boolean
  id?: string
  className?: string
  /** Extra toolbar content on the right (e.g. <AiWriteButton/>). */
  toolbarEnd?: React.ReactNode
  'aria-label'?: string
}

const Impl = lazy(() => import('./rich-text-impl'))

/**
 * WYSIWYG for descriptions/pages: bold, italic, strike, links, headings, lists, quotes, images,
 * undo/redo, HTML source view. `<RichTextEditor value={html} onChange={setHtml} onUploadImage={uploadAndGetUrl} />`
 */
export function RichTextEditor(props: RichTextEditorProps) {
  return (
    <Suspense fallback={
      <div className={cn('flex flex-col overflow-hidden rounded-control border border-input bg-surface', props.className)} aria-busy>
        <div className="flex h-10 items-center gap-2 border-b border-border px-2">{Array.from({ length: 7 }, (_, i) => <Skeleton key={i} className="size-6" />)}</div>
        <div className="p-3" style={{ minHeight: props.minHeight ?? 160 }}><Skeleton className="h-3 w-2/3" /></div>
      </div>
    }>
      <Impl {...props} />
    </Suspense>
  )
}
